import { useCallback, useMemo, useRef } from "react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { Image } from "lucide-react";

import { cn } from "utils/helpers";
import { useAppSelector } from "utils/store";
import { resolveImageSrc } from "utils/templates/image-manifest";

import { ImageBlockTypes } from "pages/tip-tap-editor/extensions/ImageBlock";

export const ImageBlockView = (props: NodeViewProps) => {
  const logoUrl = useAppSelector((state) => state.templateBuilderSlice.logoUrl);
  const storageToken = useAppSelector((state) => state.templateBuilderSlice.storageToken);

  const { editor, getPos, node, updateAttributes } = props;
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const { src, type, imageId } = node.attrs;

  const manifestEntry = useAppSelector((state) =>
    state.templateBuilderSlice.imageManifest.find((e) => e.id === imageId)
  );

  const wrapperClassName = cn(
    node.attrs.align === "left" ? "ml-0" : "ml-auto",
    node.attrs.align === "right" ? "mr-0" : "mr-auto",
    node.attrs.align === "center" && "mx-auto"
  );

  const imgSrc = useMemo(() => {
    if (type === ImageBlockTypes.LOGO) {
      return logoUrl ?? src;
    }
    if (type === ImageBlockTypes.PROJECT_IMAGE) {
      return src;
    }
    return resolveImageSrc(manifestEntry, storageToken);
  }, [logoUrl, storageToken, src, type, manifestEntry]);

  const onClick = useCallback(() => {
    editor.commands.setNodeSelection(getPos());
  }, [getPos, editor.commands]);

  /**
   * Capture the real image's aspect ratio once it loads, so the PDF renderer can
   * reserve exact vertical space. The project image is a fixed "standard size"
   * slot, so it keeps its type default rather than capturing the placeholder.
   */
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (type === ImageBlockTypes.PROJECT_IMAGE) return;
      if (node.attrs.aspectRatio) return;
      const img = e.currentTarget;
      if (img.naturalWidth > 0) {
        updateAttributes({ aspectRatio: img.naturalHeight / img.naturalWidth });
      }
    },
    [type, node.attrs.aspectRatio, updateAttributes]
  );

  return (
    <NodeViewWrapper>
      <div className={wrapperClassName} style={{ width: node.attrs.width, background: "var(--surface-100)" }}>
        <div className="rounded-lg overflow-hidden" contentEditable={false} ref={imageWrapperRef}>
          {imgSrc && <img className="block" src={imgSrc} alt="" onClick={onClick} onLoad={onImageLoad} />}
          {!imgSrc && (
            <div
              className="flex flex-col items-center justify-center"
              style={{ color: "var(--surface-600)", padding: "calc(1.5rem * var(--editor-scale))" }}
            >
              <div>
                <Image
                  className="w-[calc(64px*var(--editor-scale))] h-[calc(64px*var(--editor-scale))]"
                  size={64}
                  strokeWidth={1}
                />
              </div>
              <p style={{ fontSize: "calc(1rem * var(--editor-scale))", margin: 0 }}>Select an image.</p>
            </div>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export default ImageBlockView;
